import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Icon, message, Modal, Spin, Tooltip } from 'antd';
import { FixedSizeList } from 'react-window';
import moment from 'moment';

import Layout from '~/components/UI/Layout/Layout';
import { ModuleHeader } from '~/components/ModuleHeader';
import CustomerForm from '~/_domains/customers/drawer/index';

import { listarDescadastros, reativarDescadastro } from '../api';
import ClienteLink from '../ClienteLink';

const GRADE = '1.15fr 1fr 1fr 104px 158px 152px';
const ALTURA_LINHA = 56;

const Pilula = ({ texto, cor, fundo, borda }) => (
  <span style={{
    display: 'inline-block', fontSize: 11.5, fontWeight: 600, lineHeight: 1.6,
    padding: '1px 9px', borderRadius: 20,
    color: cor, background: fundo, border: '1px solid ' + borda, whiteSpace: 'nowrap',
  }}>
    {texto}
  </span>
);

/**
 * Quem pediu para sair das campanhas — e como devolver alguém à lista.
 *
 * O aviso do topo não é decoração: a dúvida "então ele parou de receber as
 * guias?" aparece toda vez que alguém abre esta tela pela primeira vez.
 */
const Descadastros = ({ history }) => {
  const [dados, setDados] = useState({ registros: [], total_ativos: 0, total_reativados: 0 });
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState('fora');
  const [busca, setBusca] = useState('');

  // Busca tudo de uma vez e filtra na tela: a lista é pequena por natureza, e
  // ir ao servidor a cada troca de filtro deixava a alternância travada.
  const carregar = useCallback(() => {
    setCarregando(true);
    listarDescadastros(true)
      .then((r) => r && setDados(r))
      .catch(() => message.error('Não foi possível carregar os descadastros.'))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const confirmarReativacao = (registro) => {
    Modal.confirm({
      title: 'Voltar a enviar campanhas para este e-mail?',
      content: (
        <div>
          <p style={{ marginBottom: 8 }}><strong>{registro.email}</strong></p>
          <p style={{ marginBottom: 0, color: '#8c8c8c' }}>
            A pessoa pediu para sair em {moment(registro.saiu_em).format('DD/MM/YYYY')}.
            Fica registrado que foi você quem desfez.
          </p>
        </div>
      ),
      okText: 'Voltar a enviar',
      cancelText: 'Cancelar',
      onOk: () => reativarDescadastro(registro.id)
        .then(() => {
          message.success('E-mail voltou a receber campanhas.');
          carregar();
        })
        .catch(() => message.error('Não foi possível reativar.')),
    });
  };

  const registros = dados.registros || [];

  const abas = useMemo(() => ([
    { chave: 'fora', rotulo: 'Fora das campanhas', total: registros.filter((r) => r.ativo).length },
    { chave: 'voltaram', rotulo: 'Voltaram a receber', total: registros.filter((r) => !r.ativo).length },
    { chave: 'todos', rotulo: 'Todos', total: registros.length },
  ]), [registros]);

  const filtrados = useMemo(() => {
    const alvo = busca.toLowerCase();
    return registros.filter((r) => {
      if (aba === 'fora' && !r.ativo) return false;
      if (aba === 'voltaram' && r.ativo) return false;
      if (!alvo) return true;
      return (r.email || '').toLowerCase().indexOf(alvo) >= 0
        || (r.cliente && (r.cliente.name || '').toLowerCase().indexOf(alvo) >= 0);
    });
  }, [registros, aba, busca]);

  const baixarCsv = () => {
    if (!registros.length) {
      message.info('Ninguém pediu descadastro ainda.');
      return;
    }
    const csv = String.fromCharCode(0xfeff) +
      [['E-mail', 'Cliente', 'Saiu pela campanha', 'Quando', 'Situação']]
        .concat(registros.map((r) => [
          r.email || '',
          (r.cliente && r.cliente.name) || '',
          (r.campanha && r.campanha.name) || '',
          r.saiu_em ? moment(r.saiu_em).format('DD/MM/YYYY') : '',
          r.ativo ? 'Fora das campanhas' : 'Voltou a receber',
        ]))
        .map((l) => l.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(';'))
        .join('\n');

    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    link.download = 'descadastros.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const Linha = ({ index, style }) => {
    const r = filtrados[index];
    return (
      <div style={{
        ...style, display: 'grid', gridTemplateColumns: GRADE, gap: 12,
        alignItems: 'center', padding: '0 18px',
        borderBottom: '1px solid #f5f5f5', background: '#fff', fontSize: 13,
      }}>
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {r.email}
        </div>

        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {r.cliente ? (
            <ClienteLink id={r.cliente.id} nome={r.cliente.name} />
          ) : <span style={{ color: '#bfbfbf' }}>—</span>}
          {r.cliente && r.cliente.cod && (
            <span style={{ color: '#bfbfbf', marginLeft: 6 }}>#{r.cliente.cod}</span>
          )}
        </div>

        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {r.campanha
            ? <Link to={'/marketing/campanhas/' + r.campanha.id + '/relatorio'}>{r.campanha.name}</Link>
            : <span style={{ color: '#bfbfbf' }}>—</span>}
        </div>

        <div style={{ color: '#595959' }}>
          {r.saiu_em ? moment(r.saiu_em).format('DD/MM/YYYY') : '—'}
        </div>

        <div>
          {r.ativo ? (
            <Pilula texto="Fora das campanhas" cor="#a8071a" fundo="#fff1f0" borda="#ffa39e" />
          ) : (
            <Tooltip title={
              'Reativado' +
              (r.reativado_em ? ' em ' + moment(r.reativado_em).format('DD/MM/YYYY') : '') +
              (r.reativado_por ? ' por ' + r.reativado_por : '')
            }>
              <span>
                <Pilula texto="Voltou a receber" cor="#237804" fundo="#f6ffed" borda="#b7eb8f" />
              </span>
            </Tooltip>
          )}
        </div>

        <div style={{ textAlign: 'right' }}>
          {r.ativo ? (
            <Button size="small" icon="undo" onClick={() => confirmarReativacao(r)}>
              Voltar a receber
            </Button>
          ) : (
            <span style={{ fontSize: 11.5, color: '#8c8c8c' }}>
              {r.reativado_em && moment(r.reativado_em).format('DD/MM')}
              {r.reativado_por && ' · ' + String(r.reativado_por).split(' ')[0]}
            </span>
          )}
        </div>
      </div>
    );
  };

  const alturaLista = Math.min(Math.max(filtrados.length, 1) * ALTURA_LINHA, 520);

  const indicador = (rotulo, valor, cor) => (
    <div style={{ flex: '0 0 auto', paddingRight: 34 }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: '#8c8c8c' }}>
        {rotulo}
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.2, color: cor, fontVariantNumeric: 'tabular-nums' }}>
        {valor}
      </div>
    </div>
  );

  return (
    <Layout>
      <ModuleHeader
        breadcrumbs={[
          'Marketing',
          { label: 'Campanhas', onClick: () => history.push('/marketing/campanhas') },
          'Descadastros',
        ]}
        title="Descadastros"
      />

      <div style={{
        background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
        padding: '16px 20px', marginBottom: 14, display: 'flex',
        alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        {indicador('Fora das campanhas', dados.total_ativos, dados.total_ativos ? '#cf1322' : '#8c8c8c')}
        {indicador('Voltaram a receber', dados.total_reativados, dados.total_reativados ? '#237804' : '#8c8c8c')}

        <div style={{
          flex: 1, minWidth: 280, borderLeft: '1px solid #f0f0f0', paddingLeft: 20,
          fontSize: 12.5, color: '#8c8c8c', lineHeight: 1.5,
        }}>
          <strong style={{ color: '#595959' }}>Vale só para campanhas.</strong> Quem saiu desta lista
          continua recebendo guias, documentos, avisos de vencimento e o acesso ao painel — o e-mail do
          serviço contratado não é opcional.
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '10px 14px', borderBottom: '1px solid #e8e8e8',
        }}>
          {abas.map((a) => (
            <button
              key={a.chave}
              onClick={() => setAba(a.chave)}
              style={{
                border: '1px solid ' + (aba === a.chave ? '#1890ff' : '#e8e8e8'),
                background: aba === a.chave ? '#e6f4ff' : '#fff',
                color: aba === a.chave ? '#0958d9' : '#595959',
                fontWeight: aba === a.chave ? 600 : 400,
                borderRadius: 20, padding: '3px 13px', fontSize: 13, cursor: 'pointer', outline: 'none',
              }}
            >
              {a.rotulo} <span style={{ opacity: .65 }}>{a.total}</span>
            </button>
          ))}

          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Input
              size="small"
              prefix={<Icon type="search" />}
              placeholder="buscar e-mail ou cliente"
              style={{ width: 220 }}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            <Button size="small" icon="download" onClick={baixarCsv}>Exportar</Button>
          </span>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: GRADE, gap: 12, padding: '9px 18px',
          background: '#fafafa', borderBottom: '1px solid #e8e8e8',
          fontSize: 11, fontWeight: 600, letterSpacing: '.05em',
          textTransform: 'uppercase', color: '#8c8c8c',
        }}>
          <div>E-mail</div><div>Cliente</div><div>Saiu pela campanha</div><div>Quando</div><div>Situação</div><div />
        </div>

        {carregando ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
        ) : filtrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{
              width: 52, height: 52, borderRadius: 15, background: '#f6ffed',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
            }}>
              <Icon type="check" style={{ fontSize: 22, color: '#52c41a' }} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, color: '#1f2430' }}>
              {registros.length === 0 ? 'Ninguém pediu descadastro' : 'Nada nesta aba'}
            </div>
            {registros.length === 0 && (
              <div style={{ fontSize: 13, color: '#8c8c8c', marginTop: 4, maxWidth: 440, margin: '4px auto 0' }}>
                Cada e-mail de campanha leva um link de saída no rodapé, como manda a LGPD.
                Quem clicar aparece aqui, com a campanha em que clicou.
              </div>
            )}
          </div>
        ) : (
          <FixedSizeList
            height={alturaLista}
            itemCount={filtrados.length}
            itemSize={ALTURA_LINHA}
            width="100%"
          >
            {Linha}
          </FixedSizeList>
        )}
      </div>

      <CustomerForm />
    </Layout>
  );
};

export default Descadastros;
